import React, { useEffect, useRef, useState } from "react";
import { auth, db, storage } from "../../firebase/config";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import {
  sendPasswordResetEmail,
  signOut,
  verifyBeforeUpdateEmail,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  ref,
  uploadBytesResumable,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { geocodeLocation, reverseGeocode } from "../../utils/geocode";
import {
  describeGeolocationError,
  getBrowserGeolocation,
  getGeolocationPermissionState,
  getGeolocationSupportStatus,
} from "../../utils/browserLocation";
import {
  LOCATION_AREA_ERROR,
  buildBroadLocationText,
  normalizeBroadLocation,
  validateBroadLocationInput,
} from "../../utils/locationArea";
import SupportLink from "../Common/SupportLink";
import InstallAppButton from "../Common/InstallAppButton";
import {
  LOCATION_SOURCES,
  buildPrivateProfileLocationFields,
  buildPublicProfileLocationFields,
  hasValidCoordinates,
  normalizeLocationRecord,
  normalizeProfileLocation,
} from "../../utils/locationUtils";
import { SERVICE_AVAILABILITY_OPTIONS } from "../../constants/availability";
import {
  STOREFRONT_AVATAR_FRAMES,
  STOREFRONT_DECORATION_LEVELS,
  STOREFRONT_THEMES,
  getStorefrontAvatarFrame,
  getStorefrontDecorationLevel,
  getStorefrontTheme,
  getStorefrontThemeConfig,
  isValidStorefrontAvatarFrame,
  isValidStorefrontDecorationLevel,
  isValidStorefrontTheme,
} from "../../utils/storefrontThemes";
import { getProfilePath, isStorefrontEnabled } from "../../utils/storefront";
import {
  buildPublicStorefrontProjection,
  PUBLIC_STOREFRONT_COLLECTION,
} from "../../utils/publicStorefront";
import FarmAvatar from "../Common/FarmAvatar";
import {
  completeTrackedFlow,
  startTrackedFlow,
  trackUsageEvent,
} from "../Analytics/CampaignTrafficTracker";
import { trackAppEvent } from "../../utils/appAnalytics";
import {
  ADDITIONAL_PROFILE_ROLE_OPTIONS,
  getPreservedSystemRole,
  getProfileRoleLabel,
  normalizeStoredProfileRoles,
  toggleProfileRole,
} from "../../utils/profileRoles";
import {
  appendPortfolioImagesToUserProfile,
  mergePortfolioImages,
  uploadPortfolioFiles,
} from "../../utils/portfolioImages";
import "./EditProfileStorefrontThemes.css";

const ROLE_OPTIONS = [
  { value: "landowner", label: "🌾 Land Owner" },
  { value: "farmer", label: "🚜 Farmer" },
  { value: "homesteader", label: "🏡 Homesteader" },
  { value: "day_worker", label: "🛠 Day Worker" },
  { value: "community", label: "🤝 Community Member" },
  { value: "event_planner", label: "🎪 Event Planner" },
];

ROLE_OPTIONS.push(...ADDITIONAL_PROFILE_ROLE_OPTIONS);

const FARM_ANIMALS = [
  { id: "goat", label: "Goat", emoji: "Goat" },
  { id: "chicken", label: "Chicken", emoji: "Chicken" },
  { id: "pig", label: "Pig", emoji: "Pig" },
  { id: "sheep", label: "Sheep", emoji: "Sheep" },
  { id: "duck", label: "Duck", emoji: "Duck" },
  { id: "rabbit", label: "Rabbit", emoji: "Rabbit" },
];

const SOUND_MAP = {
  default: null,
  goat: "/sounds/goat.mp3",
  cow: "/sounds/cow.wav",
  chicken: "/sounds/chicken.mp3",
  rooster: "/sounds/rooster.mp3",
  horse: "/sounds/horse.mp3",
  pig: "/sounds/pig.mp3",
  duck: "/sounds/duck.mp3",
  donkey: "/sounds/donkey.mp3",
};

const formatRoleName = getProfileRoleLabel;

const getSavedLocation = (profileData = {}) => {
  return normalizeProfileLocation(profileData) || normalizeLocationRecord({});
};

const accountAccessActionSettings = {
  url: "https://myfarmingpal.com/login",
  handleCodeInApp: false,
};

const safeStorageFileName = (name = "profile.jpg") =>
  String(name || "profile.jpg")
    .trim()
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .slice(0, 120) || "profile.jpg";

const getImageContentType = (file) => {
  const explicitType = String(file?.type || "").toLowerCase();
  if (explicitType.startsWith("image/")) return explicitType;

  const name = String(file?.name || "").toLowerCase();
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".webp")) return "image/webp";
  if (name.endsWith(".gif")) return "image/gif";
  if (name.endsWith(".heic")) return "image/heic";
  if (name.endsWith(".heif")) return "image/heif";

  return "application/octet-stream";
};

const syncPublicStorefrontProjection = async (userId, profile) => {
  const projection = buildPublicStorefrontProjection(profile, userId);
  const existingSnapshot = await getDocs(
    query(
      collection(db, PUBLIC_STOREFRONT_COLLECTION),
      where("ownerId", "==", userId)
    )
  );

  await Promise.all(
    existingSnapshot.docs
      .filter((snapshot) => snapshot.id !== projection?.slug)
      .map((snapshot) =>
        setDoc(
          doc(db, PUBLIC_STOREFRONT_COLLECTION, snapshot.id),
          {
            storefrontEnabled: false,
            isPublic: false,
            status: "hidden",
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        )
      )
  );

  if (!projection || projection.status !== "active") return;

  await setDoc(
    doc(db, PUBLIC_STOREFRONT_COLLECTION, projection.slug),
    { ...projection, updatedAt: serverTimestamp() },
    { merge: true }
  );
};

const logProfilePhotoUploadDebug = (label, details) => {
  if (process.env.NODE_ENV === "production") return;
  console.info(`[profile-photo-upload] ${label}`, details);
};

const logProfileFirestoreSaveDebug = (label, details) => {
  if (process.env.NODE_ENV === "production") return;
  console.info(`[profile-firestore-save] ${label}`, details);
};

const getReadableLocationLabel = (locationData = {}) =>
  locationData.locationText ||
  [locationData.city || locationData.county, locationData.state]
    .filter(Boolean)
    .join(", ") ||
  "your saved area";

const getPublicLocationProfilePayload = (locationData = {}) => {
  const publicFields = buildPublicProfileLocationFields(locationData);

  return {
    ...publicFields,
    location: {
      ...publicFields,
      updatedAt: serverTimestamp(),
    },
    locationUpdatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
};

const getPrivateLocationProfilePayload = (locationData = {}) => ({
  ...buildPrivateProfileLocationFields(locationData),
  updatedAt: serverTimestamp(),
});

const EditProfile = ({ setAppUserLocation }) => {
  const [image, setImage] = useState(null);
  const [removeProfilePicture, setRemoveProfilePicture] = useState(false);
  const [preview, setPreview] = useState(null);

  const [name, setName] = useState("");
  const [roles, setRoles] = useState(["farmer"]);
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState("");
  const [intro, setIntro] = useState("");
  const [serviceAvailabilityStatus, setServiceAvailabilityStatus] = useState("available");
  const [availableStarting, setAvailableStarting] = useState("");
  const [seasonalAvailabilityNote, setSeasonalAvailabilityNote] = useState("");

  const [workImages, setWorkImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  const [selectedSound, setSelectedSound] = useState("default");
  const [farmAnimal, setFarmAnimal] = useState("goat");
  const [farmAnimalSelectionExplicit, setFarmAnimalSelectionExplicit] =
    useState(false);
  const [savedLocation, setSavedLocation] = useState(null);
  const [locationInput, setLocationInput] = useState("");
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationMessage, setLocationMessage] = useState("");
  const [locationMessageType, setLocationMessageType] = useState("info");

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [profileSaveMessage, setProfileSaveMessage] = useState("");
  const [portfolioSaving, setPortfolioSaving] = useState(false);
  const [portfolioSaveMessage, setPortfolioSaveMessage] = useState("");
  const [accountMessage, setAccountMessage] = useState("");
  const [accountWorking, setAccountWorking] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [selectedStorefrontTheme, setSelectedStorefrontTheme] = useState(
    getStorefrontTheme()
  );
  const [savedStorefrontTheme, setSavedStorefrontTheme] = useState(
    getStorefrontTheme()
  );
  const [selectedStorefrontAvatarFrame, setSelectedStorefrontAvatarFrame] =
    useState(getStorefrontAvatarFrame());
  const [savedStorefrontAvatarFrame, setSavedStorefrontAvatarFrame] =
    useState(getStorefrontAvatarFrame());
  const [selectedStorefrontDecorationLevel, setSelectedStorefrontDecorationLevel] =
    useState(getStorefrontDecorationLevel());
  const [savedStorefrontDecorationLevel, setSavedStorefrontDecorationLevel] =
    useState(getStorefrontDecorationLevel());
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeMessage, setThemeMessage] = useState("");
  const [storefrontEnabled, setStorefrontEnabled] = useState(false);
  const [storefrontSetupOpen, setStorefrontSetupOpen] = useState(false);

  const fileInputRef = useRef(null);
  const portfolioInputRef = useRef(null);

  const navigate = useNavigate();
  const user = auth.currentUser;

  useEffect(() => {
    if (!user?.uid) return;

    startTrackedFlow("profile_edit", {
      feature: "profile",
      action: "edit",
      eventType: "profile_edit_started",
    });
    trackUsageEvent("profile_edit_started", {
      feature: "profile",
      action: "edit",
    });
    trackAppEvent("profile_started", {
      feature: "profile",
      action: "edit",
    });
  }, [user?.uid]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;


      
      const docRef = doc(db, "users", user.uid);
      const snap = await getDoc(docRef);

      if (!snap.exists()) return;

      const data = snap.data();

      setName(data.name || "");
      setPhone(data.phone || "");
      setBio(data.bio || "");
      setIntro(data.intro || "");
      setServiceAvailabilityStatus(data.serviceAvailabilityStatus || "available");
      setAvailableStarting(data.availableStarting || "");
      setSeasonalAvailabilityNote(data.seasonalAvailabilityNote || "");

      if (Array.isArray(data.roles)) {
        setRoles(data.roles);
      } else if (data.type) {
        setRoles([data.type]);
      } else {
        setRoles(["farmer"]);
      }

      if (data.profilePicture) {
        setPreview(data.profilePicture);
        setRemoveProfilePicture(false);
      }

      if (Array.isArray(data.workImages)) {
        setExistingImages(data.workImages);
      }

      if (data.notifPrefs?.sound) {
        setSelectedSound(data.notifPrefs.sound);
      }

      setFarmAnimal(data.farmAnimal || "goat");
      setFarmAnimalSelectionExplicit(data.farmAnimalSelectionExplicit === true);
      const profileTheme = getStorefrontTheme(data);
      const profileThemeConfig = getStorefrontThemeConfig(profileTheme);
      const profileAvatarFrame = getStorefrontAvatarFrame(
        data,
        profileThemeConfig
      );
      const profileDecorationLevel = getStorefrontDecorationLevel(data);
      setSelectedStorefrontTheme(profileTheme);
      setSavedStorefrontTheme(profileTheme);
      setSelectedStorefrontAvatarFrame(profileAvatarFrame);
      setSavedStorefrontAvatarFrame(profileAvatarFrame);
      setSelectedStorefrontDecorationLevel(profileDecorationLevel);
      setSavedStorefrontDecorationLevel(profileDecorationLevel);
      const effectiveStorefrontEnabled = isStorefrontEnabled(data);
      setStorefrontEnabled(effectiveStorefrontEnabled);
      setStorefrontSetupOpen(effectiveStorefrontEnabled);

      const currentLocation = getSavedLocation(data);
      setSavedLocation(currentLocation);
      setLocationInput(buildBroadLocationText(currentLocation));
    };

    fetchProfile();
  }, [user]);

  useEffect(() => {
    if (window.location.hash !== "#profile-location-section") return;

    const timeoutId = setTimeout(() => {
      document
        .getElementById("profile-location-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [user]);

  useEffect(() => {
    if (!image) return;

    const url = URL.createObjectURL(image);
    setPreview(url);
    setRemoveProfilePicture(false);

    return () => URL.revokeObjectURL(url);
  }, [image]);

  useEffect(() => {
    if (workImages.length === 0) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue =
        "These portfolio photos are not saved yet. Leave without uploading them?";
      return event.returnValue;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [workImages.length]);

  const handleTestSound = async () => {
    try {
      if (selectedSound === "default") return;

      const path = SOUND_MAP[selectedSound];
      if (!path) return;

      const audio = new Audio(path);
      audio.volume = 0.6;

      await audio.play();
    } catch (err) {
      console.log("Test sound failed:", err);
    }
  };

  const toggleRole = (roleValue, checked) => {
    setRoles((prev) => toggleProfileRole(prev, roleValue, checked));
  };

  const handleProfilePictureChange = (file) => {
    if (!file) return;
    setImage(file);
    setRemoveProfilePicture(false);
  };

  const handleRemoveProfilePicture = () => {
    setImage(null);
    setPreview(null);
    setRemoveProfilePicture(true);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePortfolioFiles = (files) => {
    const selected = Array.from(files || []);
    if (selected.length === 0) return;

    setWorkImages((prev) => [...prev, ...selected]);

    if (portfolioInputRef.current) {
      portfolioInputRef.current.value = "";
    }
  };

  const removePendingPortfolioImage = (index) => {
    setWorkImages((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadPendingPortfolioImages = async (filesToUpload) =>
    uploadPortfolioFiles({
      files: filesToUpload,
      userId: user.uid,
      storage,
      storageRef: ref,
      uploadBytesResumable,
      getDownloadURL,
      safeFileName: safeStorageFileName,
      onProgress: setProgress,
    });

  const confirmDiscardPendingPortfolio = () => {
    if (workImages.length === 0) return true;

    return window.confirm(
      "These portfolio photos are not saved yet. Leave without uploading them?"
    );
  };

  const handleSavePortfolioImages = async () => {
    if (!user?.uid) {
      setPortfolioSaveMessage("Please log in before uploading portfolio photos.");
      return;
    }

    const pendingImages = [...workImages];
    if (pendingImages.length === 0) return;

    try {
      setPortfolioSaving(true);
      setUploading(true);
      setProgress(0);
      setPortfolioSaveMessage("");

      const uploadedImages = await uploadPendingPortfolioImages(pendingImages);
      const { updatedWorkImages } = await appendPortfolioImagesToUserProfile({
        db,
        userId: user.uid,
        firestoreDoc: doc,
        getDoc,
        setDoc,
        serverTimestamp,
        uploadedImages,
      });

      setExistingImages(updatedWorkImages);
      setWorkImages([]);
      setProgress(0);
      setPortfolioSaveMessage("Portfolio photos uploaded and saved.");
    } catch (error) {
      console.error("Portfolio photo save failed:", error);
      setPortfolioSaveMessage(
        "Portfolio photos could not be uploaded. Please try again."
      );
    } finally {
      setPortfolioSaving(false);
      setUploading(false);
    }
  };

  const showLocationMessage = (message, type = "info") => {
    setLocationMessage(message);
    setLocationMessageType(type);
  };

  const saveLocationData = async (locationData, source) => {
    if (!user?.uid) return;

    const cleanLocation = {
      ...locationData,
      locationSource: source,
    };
    const normalizedLocation = normalizeLocationRecord(cleanLocation, cleanLocation.locationText);
    const locationText = buildBroadLocationText(normalizedLocation);

    const nextSavedLocation = {
      ...normalizedLocation,
      locationText,
      locationSource: source,
    };

    const userRef = doc(db, "users", user.uid);
    const batch = writeBatch(db);

    batch.update(userRef, getPublicLocationProfilePayload(nextSavedLocation));

    if (hasValidCoordinates(nextSavedLocation)) {
      batch.set(
        doc(db, "users", user.uid, "private", "location"),
        getPrivateLocationProfilePayload(nextSavedLocation),
        { merge: true }
      );
    }

    await batch.commit();

    setSavedLocation(nextSavedLocation);
    setLocationInput(locationText);

    if (
      typeof setAppUserLocation === "function" &&
      hasValidCoordinates(nextSavedLocation)
    ) {
      setAppUserLocation(nextSavedLocation);
      showLocationMessage(
        `Location saved as ${getReadableLocationLabel(nextSavedLocation)}. Nearby results will update now.`,
        "success"
      );
    } else if (!hasValidCoordinates(nextSavedLocation)) {
      showLocationMessage(
        "Area saved without map coordinates. You can correct it later if nearby results look off.",
        "warning"
      );
    } else {
      showLocationMessage(
        `Location saved as ${getReadableLocationLabel(nextSavedLocation)}. Refresh or return home to update nearby results.`,
        "success"
      );
    }
  };

  const handleSaveManualLocation = async () => {
    if (!user?.uid) {
      showLocationMessage("Please log in before saving a location.", "error");
      return;
    }

    const cleanInput = locationInput.trim();
    if (!cleanInput) {
      showLocationMessage("Please enter a county/state or city/state area.", "error");
      return;
    }

    const validation = validateBroadLocationInput(cleanInput);
    if (!validation.ok) {
      showLocationMessage(validation.error || LOCATION_AREA_ERROR, "error");
      return;
    }

    try {
      setIsSavingLocation(true);
      showLocationMessage("");

      let locationData = {
        ...normalizeBroadLocation({ locationText: cleanInput }, cleanInput),
      };

      const geocoded = await geocodeLocation(cleanInput);
      if (geocoded && typeof geocoded.lat === "number" && typeof geocoded.lng === "number") {
        const reverseLocation = await reverseGeocode(geocoded.lat, geocoded.lng);
        locationData = {
          lat: geocoded.lat,
          lng: geocoded.lng,
          ...normalizeBroadLocation(reverseLocation || {}, cleanInput),
        };
      } else {
        showLocationMessage(
          "Area saved without map coordinates. You can correct it later if nearby results look off.",
          "warning"
        );
      }

      await saveLocationData(locationData, LOCATION_SOURCES.EXPLICIT);
    } catch (error) {
      console.error("Failed saving location:", error);
      showLocationMessage(
        "Could not save that location right now. Please try again, or contact support if it keeps happening.",
        "error"
      );
    } finally {
      setIsSavingLocation(false);
    }
  };

  const handleUseCurrentLocation = async () => {
    showLocationMessage("");

    const support = getGeolocationSupportStatus();
    if (!support.ok) {
      const details = describeGeolocationError(support);
      const helpText = Array.isArray(details.helpText)
        ? details.helpText.join(" ")
        : details.helpText;
      showLocationMessage([details.message, helpText].filter(Boolean).join(" "), "error");
      return;
    }

    try {
      setIsLocating(true);

      const permissionState = await getGeolocationPermissionState();
      if (permissionState === "denied") {
        throw { code: "permission_denied" };
      }

      const position = await getBrowserGeolocation();
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const reverseLocation = await reverseGeocode(lat, lng);

      const locationData = {
        lat,
        lng,
        ...normalizeBroadLocation(reverseLocation || {}, ""),
      };

      try {
        await saveLocationData(locationData, LOCATION_SOURCES.BROWSER_GPS);
      } catch (saveError) {
        console.error("Current location resolved but could not be saved:", saveError);
        showLocationMessage(
          "We found your general area, but could not save it right now. Please try again, or enter your city/county manually.",
          "error"
        );
      }
    } catch (error) {
      const details = describeGeolocationError(error);
      console.warn("Profile current-location lookup failed", {
        errorCode: details.code,
        errorMessage: error?.message || "",
      });
      const helpText = Array.isArray(details.helpText)
        ? details.helpText.join(" ")
        : details.helpText;
      showLocationMessage([details.message, helpText].filter(Boolean).join(" "), "error");
    } finally {
      setIsLocating(false);
    }
  };

  const handleSave = async () => {
    if (!user) return alert("User not loaded");

    if (!name.trim()) {
      alert("Please add your name.");
      return;
    }

    setProfileSaveMessage("");

    let uploadedProfileImageRef = null;
    let uploadedProfileImagePath = "";

    try {
      let workImageUrls = [];

      if (workImages.length > 0) {
        setUploading(true);
        workImageUrls = await uploadPendingPortfolioImages(workImages);

        setUploading(false);
      }

      let imageUrl;

      if (image) {
        setUploading(true);
        const profilePhotoUploadPath =
          `profilePictures/${user.uid}/${Date.now()}_${safeStorageFileName(image.name)}`;
        const profilePhotoContentType = getImageContentType(image);
        const imageRef = ref(
          storage,
          profilePhotoUploadPath
        );
        uploadedProfileImageRef = imageRef;
        uploadedProfileImagePath = profilePhotoUploadPath;
        logProfilePhotoUploadDebug("starting", {
          authUid: auth.currentUser?.uid || null,
          pathUid: user.uid,
          storageBucket: storage?.app?.options?.storageBucket || "",
          uploadPath: profilePhotoUploadPath,
          fileType: image.type || "",
          uploadContentType: profilePhotoContentType,
          fileSize: image.size || 0,
          signedIn: Boolean(auth.currentUser?.uid),
          emailVerified: auth.currentUser?.emailVerified === true,
        });
        await uploadBytes(imageRef, image, { contentType: profilePhotoContentType });
        imageUrl = await getDownloadURL(imageRef);
        setUploading(false);
      }

      const snap = await getDoc(doc(db, "users", user.uid));
      const existingData = snap.exists() ? snap.data() : {};

      const finalProfilePicture = removeProfilePicture
        ? null
        : imageUrl || existingData.profilePicture || null;

      const updatedWorkImages = mergePortfolioImages(
        existingData.workImages,
        workImageUrls
      );

      const safeRoles = normalizeStoredProfileRoles(roles);
      const primaryRole = getPreservedSystemRole({
        existingRole: existingData.role,
        existingType: existingData.type,
        selectedRoles: safeRoles,
      });

      const data = {
        name: name.trim(),
        roles: safeRoles.length ? safeRoles : ["farmer"],
        type: primaryRole,
        role: primaryRole,
        bio,
        intro,
        serviceAvailabilityStatus,
        availableStarting:
          serviceAvailabilityStatus === "seasonal" ||
          serviceAvailabilityStatus === "limited"
            ? availableStarting
            : "",
        seasonalAvailabilityNote: seasonalAvailabilityNote.trim(),
        phone: phone.trim(),
        farmAnimal,
        storefrontEnabled,
        profilePicture: finalProfilePicture,
        workImages: updatedWorkImages,
        isPublic: true,
        
        updatedAt: serverTimestamp(),
        notifPrefs: {
          sound: selectedSound,
        },
      };

      if (
        farmAnimalSelectionExplicit ||
        existingData.farmAnimalSelectionExplicit === true
      ) {
        data.farmAnimalSelectionExplicit = true;
      }

      logProfileFirestoreSaveDebug("starting", {
        authUid: auth.currentUser?.uid || null,
        docPath: `users/${user.uid}`,
        docId: user.uid,
        authUidMatchesDocId: auth.currentUser?.uid === user.uid,
        writeMethod: "setDoc",
        merge: true,
        updateKeys: Object.keys(data).sort(),
      });

      await setDoc(doc(db, "users", user.uid), data, { merge: true });

      try {
        await syncPublicStorefrontProjection(user.uid, {
          ...existingData,
          ...data,
        });
      } catch (projectionError) {
        console.error("Public storefront projection sync failed:", projectionError);
      }

      setExistingImages(updatedWorkImages);
      setWorkImages([]);
      setImage(null);
      setRemoveProfilePicture(false);
      setProgress(0);

      completeTrackedFlow("profile_edit");
      trackUsageEvent("profile_edit_saved", {
        feature: "profile",
        action: "save",
      });
      trackAppEvent("profile_completed", {
        feature: "profile",
        action: "save",
      });
      const mergedProfileForCompletion = { ...existingData, ...data };
      if (
        finalProfilePicture &&
        mergedProfileForCompletion.name &&
        (mergedProfileForCompletion.bio || mergedProfileForCompletion.intro) &&
        (
          mergedProfileForCompletion.city ||
          mergedProfileForCompletion.state ||
          mergedProfileForCompletion.county ||
          mergedProfileForCompletion.locationText ||
          mergedProfileForCompletion.location
        )
      ) {
        trackUsageEvent("profile_setup_completed", {
          feature: "profile",
          action: "complete_setup",
        });
      }

      alert("Profile saved!");
      navigate(getProfilePath({ id: user.uid, uid: user.uid, ...data }));
    } catch (error) {
      console.error("Profile save failed:", error);
      if (String(error?.code || "") === "permission-denied") {
        logProfileFirestoreSaveDebug("failed", {
          errorCode: error?.code || "",
          errorMessage: error?.message || "",
          authUid: auth.currentUser?.uid || null,
          docPath: `users/${user.uid}`,
          docId: user.uid,
          authUidMatchesDocId: auth.currentUser?.uid === user.uid,
          writeMethod: "setDoc",
          merge: true,
        });
      }
      if (String(error?.code || "").startsWith("storage/")) {
        logProfilePhotoUploadDebug("failed", {
          errorCode: error?.code || "",
          errorMessage: error?.message || "",
          authUid: auth.currentUser?.uid || null,
          pathUid: user.uid,
          storageBucket: storage?.app?.options?.storageBucket || "",
          uploadPath: image
            ? `profilePictures/${user.uid}/[timestamp]_${safeStorageFileName(image.name)}`
            : "",
          fileType: image?.type || "",
          uploadContentType: image ? getImageContentType(image) : "",
          fileSize: image?.size || 0,
          signedIn: Boolean(auth.currentUser?.uid),
          emailVerified: auth.currentUser?.emailVerified === true,
        });
      }
      if (
        uploadedProfileImageRef &&
        !String(error?.code || "").startsWith("storage/")
      ) {
        try {
          await deleteObject(uploadedProfileImageRef);
          logProfilePhotoUploadDebug("rolled-back-after-firestore-failure", {
            uploadPath: uploadedProfileImagePath,
          });
        } catch (deleteError) {
          logProfilePhotoUploadDebug("rollback-delete-failed", {
            uploadPath: uploadedProfileImagePath,
            errorCode: deleteError?.code || "",
            errorMessage: deleteError?.message || "",
          });
        }
      }
      const isStorageError = String(error?.code || "").startsWith("storage/");
      setProfileSaveMessage(
        isStorageError
          ? "Profile photo could not be uploaded. Please try again."
          : "Profile could not be saved. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSaveStorefrontTheme = async () => {
    if (!user?.uid) return;

    if (!isValidStorefrontTheme(selectedStorefrontTheme)) {
      setThemeMessage("Please choose one of the available profile styles.");
      return;
    }

    if (!isValidStorefrontAvatarFrame(selectedStorefrontAvatarFrame)) {
      setThemeMessage("Please choose one of the available avatar frames.");
      return;
    }

    if (!isValidStorefrontDecorationLevel(selectedStorefrontDecorationLevel)) {
      setThemeMessage("Please choose one of the available decoration levels.");
      return;
    }

    try {
      setThemeSaving(true);
      setThemeMessage("");
      await updateDoc(doc(db, "users", user.uid), {
        storefrontEnabled,
        storefrontTheme: selectedStorefrontTheme,
        storefrontAvatarFrame: selectedStorefrontAvatarFrame,
        storefrontDecorationLevel: selectedStorefrontDecorationLevel,
        updatedAt: serverTimestamp(),
      });

      try {
        const profileSnap = await getDoc(doc(db, "users", user.uid));
        await syncPublicStorefrontProjection(user.uid, {
          ...(profileSnap.exists() ? profileSnap.data() : {}),
          storefrontEnabled,
          storefrontTheme: selectedStorefrontTheme,
          storefrontAvatarFrame: selectedStorefrontAvatarFrame,
          storefrontDecorationLevel: selectedStorefrontDecorationLevel,
        });
      } catch (projectionError) {
        console.error("Public storefront projection sync failed:", projectionError);
      }
      setSavedStorefrontTheme(selectedStorefrontTheme);
      setSavedStorefrontAvatarFrame(selectedStorefrontAvatarFrame);
      setSavedStorefrontDecorationLevel(selectedStorefrontDecorationLevel);
      setThemeMessage("Profile appearance saved.");
      setThemePickerOpen(false);
    } catch (error) {
      console.error("Failed saving storefront theme:", error);
      setThemeMessage("Could not save profile appearance. Please try again.");
    } finally {
      setThemeSaving(false);
    }
  };

  const handleDeletePortfolioImage = async (index) => {
    if (!user?.uid) return;

    const confirmDelete = window.confirm(
      "Remove this photo from your Farm Portfolio?"
    );

    if (!confirmDelete) return;

    const imageToRemove = existingImages[index];
    const updated = existingImages.filter((_, i) => i !== index);

    setExistingImages(updated);

    await setDoc(
      doc(db, "users", user.uid),
      { workImages: updated, updatedAt: serverTimestamp() },
      { merge: true }
    );

    if (imageToRemove?.url) {
      try {
        await deleteObject(ref(storage, imageToRemove.url));
      } catch (err) {
        console.log("Storage image delete skipped or failed:", err);
      }
    }
  };

  const handleCaption = async (index, value) => {
    const updated = [...existingImages];
    updated[index] = {
      ...updated[index],
      caption: value,
    };

    setExistingImages(updated);

    await setDoc(
      doc(db, "users", user.uid),
      { workImages: updated, updatedAt: serverTimestamp() },
      { merge: true }
    );
  };

  const handleFeature = async (index) => {
    const updated = existingImages.map((img, i) => ({
      ...img,
      featured: i === index,
    }));

    setExistingImages(updated);

    await setDoc(
      doc(db, "users", user.uid),
      { workImages: updated, updatedAt: serverTimestamp() },
      { merge: true }
    );
  };

  const sendResetSafely = async (emailAddress) => {
    try {
      await sendPasswordResetEmail(auth, emailAddress, accountAccessActionSettings);
    } catch (error) {
      if (error?.code === "auth/unauthorized-continue-uri") {
        await sendPasswordResetEmail(auth, emailAddress);
        return;
      }

      throw error;
    }
  };

  const handleAccountPasswordReset = async () => {
    if (!user?.email) {
      setAccountMessage("We could not find an email on this account.");
      return;
    }

    try {
      setAccountWorking(true);
      setAccountMessage("");
      await sendResetSafely(user.email);
      setAccountMessage("Password reset instructions were sent to your account email.");
    } catch (error) {
      console.error("Account password reset failed:", error);
      setAccountMessage("Could not send the reset email right now. Please try again.");
    } finally {
      setAccountWorking(false);
    }
  };

  const handleChangeEmail = async (event) => {
    event.preventDefault();

    const cleanEmail = newEmail.trim().toLowerCase();

    if (!cleanEmail) {
      setAccountMessage("Enter the new email address you want to use.");
      return;
    }

    if (!user) {
      setAccountMessage("Please log in again before changing your email.");
      return;
    }

    try {
      setAccountWorking(true);
      setAccountMessage("");
      await verifyBeforeUpdateEmail(user, cleanEmail, accountAccessActionSettings);
      setNewEmail("");
      setAccountMessage("Check your new email to confirm the change.");
    } catch (error) {
      console.error("Change email failed:", error);
      if (error?.code === "auth/requires-recent-login") {
        setAccountMessage(
          "For security, please log out and back in before changing your email."
        );
      } else if (error?.code === "auth/unauthorized-continue-uri") {
        try {
          await verifyBeforeUpdateEmail(user, cleanEmail);
          setNewEmail("");
          setAccountMessage("Check your new email to confirm the change.");
        } catch (fallbackError) {
          console.error("Change email retry failed:", fallbackError);
          setAccountMessage("Could not start the email change. Please try again.");
        }
      } else {
        setAccountMessage("Could not start the email change. Please try again.");
      }
    } finally {
      setAccountWorking(false);
    }
  };

  const handleDeactivateAccount = async () => {
    if (!user?.uid) {
      alert("User not loaded.");
      return;
    }

    const firstConfirm = window.confirm(
      "Are you sure you want to deactivate your account? Your profile will be hidden and you will be signed out."
    );

    if (!firstConfirm) return;

    const typed = window.prompt(
      'Type "DEACTIVATE" to confirm. This will hide your profile and disable subscription access.'
    );

    if (typed !== "DEACTIVATE") {
      alert("Account deactivation cancelled.");
      return;
    }

    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        throw new Error("User profile not found.");
      }

      const userData = userSnap.data() || {};

      await updateDoc(userRef, {
        accountStatus: "deactivated",
        isPublic: false,
        name: "Deactivated User",
        bio: "",
        intro: "",
        phone: "",
        profilePicture: null,
        founderSpotActive: false,
        premiumAccess: false,
        subscriptionStatus: "deactivated",
        subscriptionRequired: true,
        founder: false,
        founderNumber: null,
        plan: "free",
        deactivatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        deactivatedProfileBackup: {
          name: userData.name || "",
          bio: userData.bio || "",
          intro: userData.intro || "",
          phone: userData.phone || "",
          profilePicture: userData.profilePicture || null,
        },
      });

      await signOut(auth);

      alert("Your account has been deactivated.");
      navigate("/");
    } catch (error) {
      console.error("Failed to deactivate account:", error);
      alert("Could not deactivate account: " + error.message);
    }
  };

  if (!user) return <div className="card">Loading profile editor...</div>;

  const selectedThemeConfig = getStorefrontThemeConfig(selectedStorefrontTheme);
  const savedThemeConfig = getStorefrontThemeConfig(savedStorefrontTheme);
  const savedAvatarFrame =
    STOREFRONT_AVATAR_FRAMES.find(
      (frame) => frame.value === savedStorefrontAvatarFrame
    ) || STOREFRONT_AVATAR_FRAMES[0];
  const savedDecorationLevel =
    STOREFRONT_DECORATION_LEVELS.find(
      (level) => level.value === savedStorefrontDecorationLevel
    ) || STOREFRONT_DECORATION_LEVELS[1];
  const storefrontCardIsSecondary =
    !storefrontEnabled &&
    roles.some((role) => role === "day_worker" || role === "community");
  const selectedAvatarFrame =
    STOREFRONT_AVATAR_FRAMES.find(
      (frame) => frame.value === selectedStorefrontAvatarFrame
    ) || STOREFRONT_AVATAR_FRAMES[0];
  const hasStorefrontStyleChanges =
    selectedStorefrontTheme !== savedStorefrontTheme ||
    selectedStorefrontAvatarFrame !== savedStorefrontAvatarFrame ||
    selectedStorefrontDecorationLevel !== savedStorefrontDecorationLevel;

  return (
    <div className="edit-profile-page page-shell page-shell-wide">
      <div className="edit-profile-header">
        <div>
          <span className="edit-profile-kicker">🌾 Profile Settings</span>
          <h2>Edit Profile</h2>
          <p>
            Update your public profile, contact details, chat animal, and Farm
            Portfolio.
          </p>
        </div>

        <div className="edit-profile-header-actions">
          <span className="map-stat-pill">
            Portfolio Photos: <strong>{existingImages.length}</strong>
          </span>
          <InstallAppButton
            variant="button"
            className="btn-soft"
            buttonLabel="Install MyFarmingPal"
            source="profile_setup"
          />
          <SupportLink className="btn-soft" />
        </div>
      </div>

      <div className="edit-profile-grid">
        <main className="edit-profile-main">
          <div className="edit-profile-group-heading">
            <span>Start Here</span>
            <h3>Profile Basics</h3>
            <p>Your name, intro, location, roles, and profile photo help people know who they are contacting.</p>
          </div>

          <section className="edit-section-card">
            <div className="edit-section-heading">
              <h3>Basic Info</h3>
              <p>This is what people see first on your profile.</p>
            </div>

            <div className="context-tip">
              <strong>Tip:</strong> Start with your name or farm name, broad
              location, short bio, and profile photo. Phone number is optional.
            </div>

            {profileSaveMessage && (
              <p className="auth-safe-message" role="alert">
                {profileSaveMessage}
              </p>
            )}

            <div className="form-stack">
              <div>
                <label className="form-label">Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="form-label">Phone Number optional</label>
                <input
                  type="tel"
                  placeholder="Example: 615-555-1234"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                <p className="input-helper">
                  This lets people call you from chat if you choose to share it.
                </p>
              </div>

              <div>
                <label className="form-label">Short Intro</label>
                <input
                  value={intro}
                  onChange={(e) => setIntro(e.target.value)}
                  placeholder="Ex: Goat milk soap maker from Tennessee"
                />
              </div>

              <div>
                <label className="form-label">Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell people about your farm, products, work, animals, equipment, and what makes you different..."
                  rows={5}
                />
              </div>

            </div>
          </section>

          <section className="edit-section-card" id="profile-location-section">
            <div className="edit-section-heading">
              <h3>Location</h3>
              <p>
                Set the area MyFarmingPal should use for nearby jobs, products,
                events, and community posts.
              </p>
            </div>

            <div className="form-stack">
              <div className="location-current-box">
                <span>Current saved location</span>
                <strong>
                  {savedLocation?.locationText ||
                    [savedLocation?.city, savedLocation?.state]
                      .filter(Boolean)
                      .join(", ") ||
                    (hasValidCoordinates(savedLocation)
                      ? "Saved current-location area"
                      : "") ||
                    "No saved location yet"}
                </strong>
              </div>

              <div>
                <label className="form-label">Location area</label>
                <input
                  type="text"
                  value={locationInput}
                  onChange={(event) => setLocationInput(event.target.value)}
                  placeholder="County, state or city, state"
                />
                <p className="input-helper">
                  Use a general area only. Please do not enter a home address.
                </p>
              </div>

              <div className="location-editor-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSaveManualLocation}
                  disabled={isSavingLocation}
                >
                  {isSavingLocation ? "Saving..." : "Save Location"}
                </button>

                <button
                  type="button"
                  className="btn-soft"
                  onClick={handleUseCurrentLocation}
                  disabled={isLocating}
                >
                  {isLocating ? "Locating..." : "Use my current location"}
                </button>
              </div>

              {locationMessage && (
                <p
                  className={`input-helper location-save-message ${locationMessageType}`}
                  role={locationMessageType === "error" ? "alert" : "status"}
                >
                  {locationMessage}
                </p>
              )}
            </div>
          </section>

          <section className="edit-section-card">
            <div className="edit-section-heading">
              <h3>Roles</h3>
              <p>Choose every role that fits what you do.</p>
            </div>

            <div className="edit-role-grid">
              {ROLE_OPTIONS.map((role) => (
                <label key={role.value} className="edit-role-card">
                  <input
                    type="checkbox"
                    checked={roles.includes(role.value)}
                    onChange={(e) => toggleRole(role.value, e.target.checked)}
                  />
                  <span>{getProfileRoleLabel(role.value)}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="edit-section-card">
            <div className="edit-section-heading">
              <h3>Profile Picture</h3>
              <p>Add a clear photo so people recognize who they are messaging.</p>
            </div>

            <div className="profile-photo-editor">
              <div className="profile-photo-preview">
                <FarmAvatar
                  profile={{
                    id: user.uid,
                    uid: user.uid,
                    name: name || "Your Name",
                    profilePicture: preview,
                    farmAnimal,
                    farmAnimalSelectionExplicit,
                  }}
                  showInitialBadge={false}
                />
              </div>

              <div className="profile-photo-actions">
                <label className="file-picker-button">
                  Change Profile Picture
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleProfilePictureChange(e.target.files?.[0])
                    }
                  />
                </label>

                {preview && (
                  <button
                    type="button"
                    className="btn-danger-soft"
                    onClick={handleRemoveProfilePicture}
                  >
                    Remove Profile Picture
                  </button>
                )}
              </div>
            </div>
            
          </section>

          <div className="edit-profile-group-heading">
            <span>Optional</span>
            <h3>Optional Extras</h3>
            <p>Add availability, portfolio photos, and public profile appearance when they are useful.</p>
          </div>

          <section className="edit-section-card">
            <div className="edit-section-heading">
              <h3>Public Availability</h3>
              <p>Optional. Use this if you want people to know whether you are currently available.</p>
            </div>

            <div className="form-panel">
              <label className="form-label">
                Availability
                <select
                  value={serviceAvailabilityStatus}
                  onChange={(e) => setServiceAvailabilityStatus(e.target.value)}
                >
                  {SERVICE_AVAILABILITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              {(serviceAvailabilityStatus === "seasonal" ||
                serviceAvailabilityStatus === "limited") && (
                <label className="form-label">
                  Available starting optional
                  <input
                    type="date"
                    value={availableStarting}
                    onChange={(e) => setAvailableStarting(e.target.value)}
                  />
                </label>
              )}

              <label className="form-label">
                Seasonal availability note optional
                <textarea
                  value={seasonalAvailabilityNote}
                  onChange={(e) => setSeasonalAvailabilityNote(e.target.value)}
                  rows={3}
                  placeholder="Example: Booking spring fencing jobs now, or hay hauling available June through August."
                />
              </label>
            </div>
          </section>

          <section
            className={`edit-section-card storefront-theme-edit-card storefront-optional-card ${
              storefrontCardIsSecondary ? "is-secondary" : ""
            }`}
          >
            <div className="edit-section-heading">
              <h3>Profile Appearance - Optional</h3>
              <p>
                Optional appearance controls for your public profile. Your regular profile is still your public page.
              </p>
            </div>

            {!storefrontSetupOpen ? (
              <button
                type="button"
                className="btn-primary storefront-setup-btn"
                onClick={() => setStorefrontSetupOpen(true)}
              >
                Customize Profile Appearance
              </button>
            ) : (
              <div className="storefront-setup-panel">
                <label className="storefront-enable-toggle">
                  <input
                    type="checkbox"
                    checked={storefrontEnabled}
                    onChange={(event) =>
                      setStorefrontEnabled(event.target.checked)
                    }
                  />
                  <span>
                    <strong>Enable enhanced public profile</strong>
                    <small>
                      Keeps your existing public profile link available after your next profile save.
                    </small>
                  </span>
                </label>

                <div className="storefront-theme-current">
                  <img
                    src={savedThemeConfig.previewCard}
                    alt={`${savedThemeConfig.name} profile appearance preview`}
                  />
                  <div>
                    <span>Current profile style</span>
                    <strong>{savedThemeConfig.name}</strong>
                    <p>{savedThemeConfig.description}</p>
                    <p className="storefront-theme-current-meta">
                      Frame: {savedAvatarFrame.label} | Decorations:{" "}
                      {savedDecorationLevel.label}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-primary storefront-theme-open-btn"
                    onClick={() => {
                      setSelectedStorefrontTheme(savedStorefrontTheme);
                      setSelectedStorefrontAvatarFrame(savedStorefrontAvatarFrame);
                      setSelectedStorefrontDecorationLevel(
                        savedStorefrontDecorationLevel
                      );
                      setThemeMessage("");
                      setThemePickerOpen(true);
                    }}
                  >
                    Choose Appearance
                  </button>
                </div>

                <p className="storefront-theme-privacy-note">
                  Privacy first: appearance changes the look of your public profile,
                  not what private information is shown.
                </p>

                {themeMessage && !themePickerOpen && (
                  <p className="storefront-theme-edit-status" role="status">
                    {themeMessage}
                  </p>
                )}
              </div>
            )}
          </section>

          <section className="edit-section-card">
            <div className="edit-section-heading">
              <h3>Farm Portfolio</h3>
              <p>
                Add photos of your farm, products, animals, equipment,
                completed jobs, or work you want people to see.
              </p>
            </div>

            <label className="file-drop-card">
              <span>📸 Add Portfolio Photos</span>
              <small>You can upload multiple images at once.</small>
              <input
                ref={portfolioInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePortfolioFiles(e.target.files)}
              />
            </label>

            {workImages.length > 0 && (
              <div className="pending-upload-panel">
                <h4>Ready to upload</h4>
                <p className="auth-safe-message" role="alert">
                  These photos are not saved yet.
                </p>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSavePortfolioImages}
                  disabled={portfolioSaving || uploading}
                >
                  {portfolioSaving
                    ? "Uploading..."
                    : "Upload & Save Portfolio Photos"}
                </button>

                <div className="portfolio-editor-grid">
                  {workImages.map((file, index) => {
                    const url = URL.createObjectURL(file);

                    return (
                      <div key={`${file.name}-${index}`} className="portfolio-card">
                        <img src={url} alt={`Selected ${index + 1}`} />

                        <div className="portfolio-card-body">
                          <strong>{file.name}</strong>

                          <button
                            type="button"
                            className="btn-danger-soft"
                            onClick={() => removePendingPortfolioImage(index)}
                          >
                            Remove Before Save
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {portfolioSaveMessage && (
              <p className="auth-safe-message" role="status">
                {portfolioSaveMessage}
              </p>
            )}

            {existingImages.length === 0 ? (
              <div className="empty-state">
                No Farm Portfolio photos yet.
              </div>
            ) : (
              <div className="portfolio-editor-grid">
                {existingImages.map((img, index) => (
                  <div key={img.url || index} className="portfolio-card">
                    <div className="portfolio-image-wrap">
                      <img
                        src={img.url}
                        alt={`Portfolio ${index + 1}`}
                        onClick={() => setSelectedImage(img.url)}
                      />

                      {img.featured && (
                        <span className="portfolio-featured-badge">
                          ⭐ Featured
                        </span>
                      )}
                    </div>

                    <div className="portfolio-card-body">
                      <input
                        type="text"
                        placeholder="Add a caption..."
                        value={img.caption || ""}
                        onChange={(e) => handleCaption(index, e.target.value)}
                      />

                      <div className="portfolio-actions">
                        <button
                          type="button"
                          className="btn-soft"
                          onClick={() => handleFeature(index)}
                        >
                          {img.featured ? "⭐ Featured" : "☆ Make Featured"}
                        </button>

                        <button
                          type="button"
                          className="btn-danger-soft"
                          onClick={() => handleDeletePortfolioImage(index)}
                        >
                          🗑 Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {uploading && (
            <section className="edit-section-card">
              <p>Uploading... {progress}%</p>

              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </section>
          )}

          <div className="edit-profile-group-heading">
            <span>Preferences</span>
            <h3>Preferences</h3>
            <p>Personalize sounds and chat identity without changing your public profile content.</p>
          </div>

          <section className="edit-section-card">
            <div className="edit-section-heading">
              <h3>Notifications & Chat Animal</h3>
              <p>
                Pick your notification sound and the farm animal that appears in
                chat.
              </p>
            </div>

            <div className="form-stack">
              <div>
                <label className="form-label">Notification Sound</label>

                <div className="sound-row">
                  <select
                    value={selectedSound}
                    onChange={(e) => setSelectedSound(e.target.value)}
                  >
                    <option value="default">Default no custom sound</option>
                    <option value="goat">🐐 Goat</option>
                    <option value="cow">🐄 Cow</option>
                    <option value="chicken">🐔 Chicken</option>
                    <option value="rooster">🐓 Rooster</option>
                    <option value="horse">🐎 Horse</option>
                    <option value="pig">🐖 Pig</option>
                    <option value="duck">🦆 Duck</option>
                    <option value="donkey">🐴 Donkey</option>
                  </select>

                  <button
                    type="button"
                    className="btn-soft"
                    onClick={handleTestSound}
                  >🔊 Test Sound</button>
                </div>
              </div>

              <div>
                <label className="form-label">Choose Your Farm Animal</label>
                <p className="input-helper">
                  This animal will show beside your chat messages and typing
                  indicator.
                </p>

                <div className="farm-animal-picker">
                  {FARM_ANIMALS.map((animal) => (
                    <button
                      type="button"
                      key={animal.id}
                      className={`farm-animal-option ${
                        farmAnimal === animal.id ? "active" : ""
                      }`}
                      onClick={() => {
                        setFarmAnimal(animal.id);
                        setFarmAnimalSelectionExplicit(true);
                      }}
                    >
                      <FarmAvatar
                        profile={{
                          uid: `farm-animal-${animal.id}`,
                          name: animal.label,
                          farmAnimal: animal.id,
                          farmAnimalSelectionExplicit: true,
                        }}
                        className="farm-animal-choice-avatar"
                        showInitialBadge={false}
                      />
                      <strong>{animal.label}</strong>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="profile-preview-card">
            <div className="profile-preview-header">
              <div className="summary-title">Live Preview</div>
            </div>

            <FarmAvatar
              profile={{
                id: user.uid,
                uid: user.uid,
                name: name || "Your Name",
                profilePicture: preview,
                farmAnimal,
                farmAnimalSelectionExplicit,
              }}
              className="avatar-large"
            />

            <h3>{name || "Your Name"}</h3>
            <p className="hero-intro">
              {intro || "Your short intro will appear here."}
            </p>

            <p className="field-card-meta">
              {roles.map(formatRoleName).join(", ")}
            </p>

            <div className="preview-animal-pill">
              <span>
                {FARM_ANIMALS.find((a) => a.id === farmAnimal)?.emoji ||
                  "🐐"}
              </span>
              Chat animal
            </div>

            <div className="profile-preview-actions">
              <button
                type="button"
                className="btn-soft"
                onClick={() => {
                  if (!confirmDiscardPendingPortfolio()) return;
                  navigate(getProfilePath({ id: user.uid, uid: user.uid, name }));
                }}
              >
                View Profile
              </button>

              <button type="button" className="btn-primary" onClick={handleSave}>
                Save Profile
              </button>
            </div>
          </section>

          <div className="edit-profile-group-heading">
            <span>Security</span>
            <h3>Account & Security</h3>
            <p>Password, email, and account controls are separate from public profile content.</p>
          </div>

          <section className="account-access-card">
            <div>
              <span className="edit-profile-kicker">Account Access</span>
              <h3>Password & Email</h3>
              <p>
                Use Firebase-secured account tools for password resets and email
                changes. MyFarmingPal never stores your password.
              </p>
            </div>

            <div className="account-access-actions">
              <button
                type="button"
                className="btn-soft"
                onClick={handleAccountPasswordReset}
                disabled={accountWorking || !user?.email}
              >
                Send Password Reset
              </button>

              <form className="account-email-form" onSubmit={handleChangeEmail}>
                <label className="form-label" htmlFor="account-new-email">
                  Change email
                </label>
                <div className="account-email-row">
                  <input
                    id="account-new-email"
                    type="email"
                    value={newEmail}
                    onChange={(event) => setNewEmail(event.target.value)}
                    placeholder="new-email@example.com"
                    autoComplete="email"
                  />
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={accountWorking}
                  >
                    Send Verification
                  </button>
                </div>
              </form>
            </div>

            {accountMessage && (
              <p className="account-access-message">{accountMessage}</p>
            )}

            <p className="account-access-help">
              Lost access to your email?{" "}
              <button
                type="button"
                onClick={() => navigate("/contact?issue=account_access")}
              >
                Contact support
              </button>
              . For security, we may need details to verify ownership.
            </p>
          </section>

          <section className="danger-zone-card">
            <h3>Account Settings</h3>
            <p>
              Need to leave MyFarmingPal? You can deactivate your account. Your
              public profile will be hidden, and if you are using an active
              founder spot, that spot will become available for someone else.
            </p>

            <button
              type="button"
              className="btn-danger-soft"
              onClick={handleDeactivateAccount}
            >
              Deactivate Account
            </button>
          </section>
        </main>
      </div>

      {themePickerOpen && (
        <div
          className="storefront-theme-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setThemePickerOpen(false);
            }
          }}
        >
          <section
            className="storefront-theme-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="storefront-theme-modal-title"
          >
            <div className="storefront-theme-modal-header">
              <div>
                <span className="edit-profile-kicker">Public Profile</span>
                <h2 id="storefront-theme-modal-title">Profile Appearance</h2>
                <p>
                  Pick a public profile style. Your content stays the same; only
                  the public presentation changes.
                </p>
              </div>

              <button
                type="button"
                className="storefront-theme-close"
                onClick={() => setThemePickerOpen(false)}
                aria-label="Close profile appearance picker"
              >
                ×
              </button>
            </div>

            <div className="storefront-theme-modal-grid">
              {STOREFRONT_THEMES.map((theme) => {
                const selected = selectedStorefrontTheme === theme.value;
                return (
                  <button
                    key={theme.value}
                    type="button"
                    className={`storefront-theme-choice ${
                      selected ? "is-selected" : ""
                    }`}
                    onClick={() => {
                      setSelectedStorefrontTheme(theme.value);
                      setSelectedStorefrontAvatarFrame(
                        theme.defaultAvatarFrameValue
                      );
                    }}
                    aria-pressed={selected}
                  >
                    <span className="storefront-theme-choice-check">
                      {selected ? "✓" : ""}
                    </span>
                    <img src={theme.previewCard} alt="" loading="lazy" />
                    <strong>{theme.name}</strong>
                    <span>{theme.description}</span>
                  </button>
                );
              })}
            </div>

            <section className="storefront-personalize-panel">
              <div className="storefront-personalize-heading">
                <div>
                  <h3>Personalize the details</h3>
                  <p>
                    Fine-tune the profile frame and how decorative your public
                    profile feels.
                  </p>
                </div>
                <span>{selectedThemeConfig.name}</span>
              </div>

              <div className="storefront-avatar-frame-picker">
                <h4>Profile frame</h4>
                <div className="storefront-avatar-frame-grid">
                  {STOREFRONT_AVATAR_FRAMES.map((frame) => {
                    const selected =
                      selectedStorefrontAvatarFrame === frame.value;
                    return (
                      <button
                        key={frame.value}
                        type="button"
                        className={`storefront-avatar-frame-option ${
                          selected ? "is-selected" : ""
                        }`}
                        onClick={() =>
                          setSelectedStorefrontAvatarFrame(frame.value)
                        }
                        aria-pressed={selected}
                      >
                        <span className="storefront-avatar-frame-sample">
                          <img src={frame.asset} alt="" loading="lazy" />
                          <span>{(name || "M").charAt(0).toUpperCase()}</span>
                        </span>
                        <strong>{frame.label}</strong>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="storefront-decoration-picker">
                <h4>Decoration level</h4>
                <div className="storefront-decoration-options">
                  {STOREFRONT_DECORATION_LEVELS.map((level) => {
                    const selected =
                      selectedStorefrontDecorationLevel === level.value;
                    return (
                      <button
                        key={level.value}
                        type="button"
                        className={`storefront-decoration-option ${
                          selected ? "is-selected" : ""
                        }`}
                        onClick={() =>
                          setSelectedStorefrontDecorationLevel(level.value)
                        }
                        aria-pressed={selected}
                      >
                        <strong>{level.label}</strong>
                        <span>{level.description}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            <div className="storefront-theme-preview-strip">
              {STOREFRONT_THEMES.map((theme) => (
                <article key={`template-${theme.value}`}>
                  <img
                    src={theme.templatePreview}
                    alt={`${theme.name} profile appearance preview`}
                    loading="lazy"
                  />
                  <strong>{theme.name}</strong>
                </article>
              ))}
            </div>

            <div className="storefront-theme-modal-bottom">
              <aside
                className="storefront-theme-tips-panel"
                style={{
                  backgroundImage: `linear-gradient(135deg, rgba(255, 250, 240, 0.92), rgba(255, 250, 240, 0.82)), url(${selectedThemeConfig.tipsCardBg})`,
                }}
              >
                <h3>Tips</h3>
                <ul>
                  <li>You can change themes anytime.</li>
                  <li>Your content stays the same.</li>
                  <li>Make it yours and keep it authentic.</li>
                </ul>
              </aside>

              <section
                className="storefront-theme-save-panel-edit"
                style={{
                  backgroundImage: `linear-gradient(135deg, rgba(255, 243, 217, 0.92), rgba(241, 215, 173, 0.82)), url(${selectedThemeConfig.savePanelBg})`,
                }}
              >
                <div>
                  <h3>Ready to update your public profile?</h3>
                  <p>Your changes will be saved and visible to the public.</p>
                  <small>
                    Selected: {selectedThemeConfig.name} with{" "}
                    {selectedAvatarFrame.label} frame
                  </small>
                </div>

                <div className="storefront-theme-save-actions">
                  <button
                    type="button"
                    className="storefront-cancel-theme-btn"
                    onClick={() => {
                      setSelectedStorefrontTheme(savedStorefrontTheme);
                      setSelectedStorefrontAvatarFrame(
                        savedStorefrontAvatarFrame
                      );
                      setSelectedStorefrontDecorationLevel(
                        savedStorefrontDecorationLevel
                      );
                      setThemePickerOpen(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="storefront-save-theme-btn"
                    onClick={handleSaveStorefrontTheme}
                    disabled={themeSaving || !hasStorefrontStyleChanges}
                  >
                    {themeSaving ? "Saving..." : "Save Theme"}
                  </button>
                </div>
              </section>
            </div>

            {themeMessage && (
              <p className="storefront-theme-edit-status" role="status">
                {themeMessage}
              </p>
            )}
          </section>
        </div>
      )}

      {selectedImage && (
        <div className="modal" onClick={() => setSelectedImage(null)}>
          <img src={selectedImage} className="modal-img" alt="preview" />
        </div>
      )}
    </div>
  );
};

export default EditProfile;
